/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { FC, useState, useEffect } from "react";
import { useFormik } from "formik";
import clsx from "clsx";
import "../../../assets/styles/custom-style.css";
import { useAuth } from "../../../auth";
import { updateBank, getBankDetailsByRoutingNumber } from "../core/_requests";
import { useNavigate, useParams } from "react-router-dom";
import ListSettlements from "./ListSettlements";
import { settlementSchema } from "../core/Schema";

interface BankAdd {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  chefId: string | undefined;
  bankId: string | undefined;
  routingNumber: string;
  accountName: string;
  nickName: string;
  accountNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssnLast4: string;
  createdBy: any;
  updatedBy: any;
  primaryId?: string;
}

type Props = {
  initialValues: BankAdd;
};

const EditSettlements: FC<Props> = ({ initialValues }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [routingNumberError, setRoutingNumberError] = useState("");
  const [submissionError, setSubmissionError] = useState("");

  const formik = useFormik<BankAdd>({
    initialValues: {
      firstName: initialValues.firstName || "",
      lastName: initialValues.lastName || "",
      email: initialValues.email || "",
      phoneNumber: initialValues.phoneNumber || "",
      dobDay: initialValues.dobDay?.toString() || "",
      dobMonth: initialValues.dobMonth?.toString() || "",
      dobYear: initialValues.dobYear?.toString() || "",
      chefId: initialValues.chefId || "",
      bankId: initialValues.bankId || "",
      routingNumber: initialValues.routingNumber || "",
      accountName: initialValues.accountName || "",
      nickName: initialValues.nickName || "",
      accountNumber: initialValues.accountNumber || "",
      address: initialValues.address || "",
      city: initialValues.city || "",
      state: initialValues.state || "",
      zipCode: initialValues.zipCode || "",
      ssnLast4: initialValues.ssnLast4 || "",
      createdBy: initialValues.createdBy || "",
      updatedBy: initialValues.updatedBy || "",
    },
    validationSchema: settlementSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      setSubmissionError("");
      const payload = {
        ...values,
        updatedBy: currentUser?.chefId,
        dobDay: parseInt(values.dobDay.toString()),
        dobMonth: parseInt(values.dobMonth.toString()),
        dobYear: parseInt(values.dobYear.toString()),
      };

      updateBank(id, payload).then((res) => {
        if (res?.status === true) {
          navigate("/settlements/editsuccess", { replace: true });
        } else {
          const msg = res?.message || "Internal server error";
          setSubmissionError(msg.replace(/Stripe /gi, "").replace(/stripe/gi, "Account"));
        }
        setLoading(false);
      }).catch((err) => {
        console.error(err);
        const errorMsg = err.response?.data?.message || err.message || "Internal server error";
        setSubmissionError(errorMsg.replace(/Stripe /gi, "").replace(/stripe/gi, "Account"));
        setLoading(false);
      });
    },
  });

  const fetchBankDetails = async () => {
    if (!formik.values.routingNumber) return;
    setLoading(true);
    try {
      const res = await getBankDetailsByRoutingNumber(formik.values.routingNumber);
      if (res?.status === "success") {
        formik.setValues({
          ...formik.values,
          bankId: res.data.name,
          city: res.data.city || formik.values.city,
          state: res.data.state || formik.values.state,
          zipCode: res.data.zip || formik.values.zipCode,
        });
        setRoutingNumberError("");
      } else {
        setRoutingNumberError("Invalid routing number");
      }
    } catch (error) {
      setRoutingNumberError("Error fetching bank details");
    }
    setLoading(false);
  };

  return (
    <section className="container-fluid addContainerFluid">
      <ListSettlements />
      <div className="overlay"></div>
      <div className="drawer wizard-drawer">
        <div className="wizard-header">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h3 className="wizard-title mb-1">Edit Account Details</h3>
              <p className="wizard-subtitle">Update your banking and identity information</p>
            </div>
            <button className="btn-close-wizard" onClick={() => navigate("/settlements")}>
              <i className="material-symbols-outlined">close</i>
            </button>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} noValidate className="wizard-form-container">
          <div className="wizard-content animate-fade-in">
            {/* SECTION 1: PERSONAL */}
            <h6 className="section-title mb-3">Personal Information</h6>
            <div className="row g-4 mb-5">
              <div className="col-md-6">
                <div className="form-group-custom">
                  <label>First Name</label>
                  <input type="text" {...formik.getFieldProps("firstName")} className={clsx("form-control-modern", { "error": formik.touched.firstName && formik.errors.firstName })} />
                  {formik.touched.firstName && formik.errors.firstName && <div className="error-msg">{formik.errors.firstName}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group-custom">
                  <label>Last Name</label>
                  <input type="text" {...formik.getFieldProps("lastName")} className={clsx("form-control-modern", { "error": formik.touched.lastName && formik.errors.lastName })} />
                  {formik.touched.lastName && formik.errors.lastName && <div className="error-msg">{formik.errors.lastName}</div>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group-custom">
                  <label>Email Address</label>
                  <input type="email" {...formik.getFieldProps("email")} className={clsx("form-control-modern", { "error": formik.touched.email && formik.errors.email })} />
                  {formik.touched.email && formik.errors.email && <div className="error-msg">{formik.errors.email}</div>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group-custom">
                  <label>Phone Number</label>
                  <input type="text" {...formik.getFieldProps("phoneNumber")} className={clsx("form-control-modern", { "error": formik.touched.phoneNumber && formik.errors.phoneNumber })} />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && <div className="error-msg">{formik.errors.phoneNumber}</div>}
                </div>
              </div>
              <div className="col-12">
                <label className="mb-2 fw-600 fs-14">Date of Birth</label>
                <div className="row g-2">
                  <div className="col-4">
                    <input type="number" {...formik.getFieldProps("dobDay")} className={clsx("form-control-modern", { "error": formik.touched.dobDay && formik.errors.dobDay })} placeholder="Day" />
                  </div>
                  <div className="col-4">
                    <input type="number" {...formik.getFieldProps("dobMonth")} className={clsx("form-control-modern", { "error": formik.touched.dobMonth && formik.errors.dobMonth })} placeholder="Month" />
                  </div>
                  <div className="col-4">
                    <input type="number" {...formik.getFieldProps("dobYear")} className={clsx("form-control-modern", { "error": formik.touched.dobYear && formik.errors.dobYear })} placeholder="Year" />
                  </div>
                </div>
                {(formik.errors.dobDay || formik.errors.dobMonth || formik.errors.dobYear) && <div className="error-msg">Please enter a valid birthday (18+)</div>}
              </div>
            </div>

            {/* SECTION 2: BANK */}
            <h6 className="section-title mb-3">Banking Details</h6>
            <div className="row g-4 mb-5">
              <div className="col-12">
                <div className="form-group-custom">
                  <label>Routing Number</label>
                  <div className="input-with-action">
                    <input type="text" {...formik.getFieldProps("routingNumber")} className={clsx("form-control-modern", { "error": formik.touched.routingNumber && (formik.errors.routingNumber || routingNumberError) })} />
                    <button type="button" onClick={fetchBankDetails} className="btn-input-action">
                      {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="material-symbols-outlined">search</i>}
                    </button>
                  </div>
                  {(formik.errors.routingNumber || routingNumberError) && <div className="error-msg">{formik.errors.routingNumber || routingNumberError}</div>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group-custom">
                  <label>Account Number</label>
                  <input type="password" {...formik.getFieldProps("accountNumber")} className={clsx("form-control-modern", { "error": formik.touched.accountNumber && formik.errors.accountNumber })} placeholder="••••••••••••" />
                  {formik.touched.accountNumber && formik.errors.accountNumber && <div className="error-msg">{formik.errors.accountNumber}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group-custom">
                  <label>Bank Name</label>
                  <input type="text" {...formik.getFieldProps("bankId")} className={clsx("form-control-modern", { "error": formik.touched.bankId && formik.errors.bankId })} />
                  {formik.touched.bankId && formik.errors.bankId && <div className="error-msg">{formik.errors.bankId}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group-custom">
                  <label>Account Holder Name</label>
                  <input type="text" {...formik.getFieldProps("accountName")} className={clsx("form-control-modern", { "error": formik.touched.accountName && formik.errors.accountName })} />
                  {formik.touched.accountName && formik.errors.accountName && <div className="error-msg">{formik.errors.accountName}</div>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group-custom">
                  <label>Internal Nickname</label>
                  <input type="text" {...formik.getFieldProps("nickName")} className={clsx("form-control-modern", { "error": formik.touched.nickName && formik.errors.nickName })} />
                </div>
              </div>
            </div>

            {/* SECTION 3: ADDRESS & IDENTITY */}
            <h6 className="section-title mb-3">Identity & Address</h6>
            <div className="row g-4">
              <div className="col-12">
                <div className="form-group-custom">
                  <label>Last 4 digits of SSN</label>
                  <input type="text" maxLength={4} {...formik.getFieldProps("ssnLast4")} className={clsx("form-control-modern", { "error": formik.touched.ssnLast4 && formik.errors.ssnLast4 })} placeholder="XXXX" />
                  {formik.touched.ssnLast4 && formik.errors.ssnLast4 && <div className="error-msg">{formik.errors.ssnLast4}</div>}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group-custom">
                  <label>Street Address</label>
                  <input type="text" {...formik.getFieldProps("address")} className={clsx("form-control-modern", { "error": formik.touched.address && formik.errors.address })} />
                  {formik.touched.address && formik.errors.address && <div className="error-msg">{formik.errors.address}</div>}
                </div>
              </div>
              <div className="col-md-5">
                <div className="form-group-custom">
                  <label>City</label>
                  <input type="text" {...formik.getFieldProps("city")} className={clsx("form-control-modern", { "error": formik.touched.city && formik.errors.city })} />
                  {formik.touched.city && formik.errors.city && <div className="error-msg">{formik.errors.city}</div>}
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group-custom">
                  <label>State</label>
                  <input type="text" {...formik.getFieldProps("state")} className={clsx("form-control-modern", { "error": formik.touched.state && formik.errors.state })} />
                  {formik.touched.state && formik.errors.state && <div className="error-msg">{formik.errors.state}</div>}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group-custom">
                  <label>Zip Code</label>
                  <input type="text" {...formik.getFieldProps("zipCode")} className={clsx("form-control-modern", { "error": formik.touched.zipCode && formik.errors.zipCode })} />
                  {formik.touched.zipCode && formik.errors.zipCode && <div className="error-msg">{formik.errors.zipCode}</div>}
                </div>
              </div>
            </div>
          </div>

          {submissionError && (
            <div className="submission-error-pill mt-4 animate-fade-in">
              <div className="d-flex align-items-center gap-3 w-100">
                <i className="material-symbols-outlined">warning</i>
                <div className="flex-grow-1">
                  <span className="d-block fw-700">{submissionError}</span>
                </div>
                {submissionError.toLowerCase().includes("exists") && (
                  <button type="button" onClick={() => navigate("/settlements")} className="btn btn-sm btn-light-success py-1 px-3 fw-bold">
                    Back
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="wizard-footer mt-5">
            <div className="d-flex justify-content-end gap-3">
              <button type="button" onClick={() => navigate("/settlements")} className="btn-wizard-back">
                Cancel
              </button>
              <button type="submit" disabled={loading || !formik.isValid} className="btn-wizard-finish">
                {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="material-symbols-outlined me-2">save</i>}
                Save Changes
              </button>
            </div>
          </div>
        </form>

        <style>{`
          .wizard-drawer { 
            background: #fff !important; 
            padding: 2.5rem !important; 
            border-radius: 20px 0 0 20px !important; 
            box-shadow: -10px 0 30px rgba(0,0,0,0.05) !important;
            width: 50% !important;
            max-width: 600px !important;
            height: 100vh !important;
            overflow-y: auto !important;
          }
          
          @media (max-width: 768px) {
            .wizard-drawer { width: 90% !important; }
          }

          .wizard-title { font-weight: 800; color: #1e293b; letter-spacing: -0.5px; }
          .wizard-subtitle { color: #64748b; font-size: 14px; }
          .btn-close-wizard { background: #f1f5f9; border: none; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; transition: 0.3s; }
          .btn-close-wizard:hover { background: #e2e8f0; transform: rotate(90deg); }

          .section-title { font-size: 14px; font-weight: 800; color: #198754; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #ecfdf5; padding-bottom: 8px; }

          .form-group-custom label { display: block; font-size: 13px; font-weight: 700; color: #334155; margin-bottom: 6px; }
          .form-control-modern { width: 100%; padding: 10px 14px; border-radius: 10px; border: 2px solid #e2e8f0; transition: 0.3s; font-size: 14px; }
          .form-control-modern:focus { border-color: #198754; outline: none; box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.1); }
          .form-control-modern.error { border-color: #ef4444; }
          .error-msg { color: #ef4444; font-size: 12px; margin-top: 4px; font-weight: 500; }

          .input-with-action { position: relative; }
          .btn-input-action { position: absolute; right: 6px; top: 6px; bottom: 6px; background: #198754; color: #fff; border: none; border-radius: 8px; padding: 0 10px; display: flex; align-items: center; }

          .wizard-footer { border-top: 1px solid #f1f5f9; padding-top: 1.5rem; }
          .btn-wizard-finish { background: #198754; color: #fff; border: none; padding: 10px 24px; border-radius: 12px; font-weight: 700; display: flex; align-items: center; gap: 8px; transition: 0.3s; }
          .btn-wizard-finish:hover { background: #157347; transform: translateY(-1px); }
          .btn-wizard-back { background: #fff; color: #64748b; border: 2px solid #e2e8f0; padding: 10px 20px; border-radius: 12px; font-weight: 700; display: flex; align-items: center; gap: 8px; }

          .submission-error-pill {
            background: #fef2f2;
            border: 1px solid #fee2e2;
            color: #b91c1c;
            padding: 12px 16px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 13px;
            font-weight: 600;
          }
          .submission-error-pill i { font-size: 22px; }
          .btn-light-success { background: #d1fae5; color: #065f46; border: none; transition: 0.2s; }

          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
          
          .fw-600 { font-weight: 600; }
          .fs-14 { font-size: 14px; }
          .wizard-form-container { max-width: 100%; }
        `}</style>
      </div>
    </section>
  );
};

export default EditSettlements;
